// Path: src/components/aols/Estate.tsx
// Updated to fix right border overlap by adjusting Content and PropertyGrid styles
// Added overflow-x: hidden to EstateWrapper to prevent horizontal overflow
// Enhanced PropertyCard with glowing border effect on hover for OLS Sun Light theme
// Improved padding, margins, and mobile responsiveness (iPhone 12, 390x844)
// Updated SEO meta tags with OLS CTAs
// 06/04/25 04:04 PM +07

import React, { useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import { Layout, Typography, Button, Breadcrumb } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import logo from "/logo.jpg";

const { Title: AntTitle } = Typography;

interface Property {
  id: string;
  price: string;
  details: string;
  image: string;
  description: string;
  features: string[];
  location: string;
}

const EstateWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%);
  overflow-x: hidden; /* Prevent horizontal overflow */
`;

const StyledLayout = styled(Layout)`
  background: transparent;
  min-height: 100vh;
`;

const HeroSection = styled.section`
  padding: 40px 20px;
  text-align: center;
  animation: fadeIn 0.5s ease-in;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px; /* Ensure padding to prevent content from touching edges */
  box-sizing: border-box; /* Include padding in width calculation */
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 20px;
  cursor: pointer;
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.05);
  }
`;

const StyledBreadcrumb = styled(Breadcrumb)`
  margin-bottom: 20px;
  a {
    color: #ffaa00;
    text-decoration: none;
    &:hover {
      color: #ffcc00;
      text-decoration: underline;
    }
  }
`;

const SectionTitle = styled(AntTitle)`
  && {
    color: #0427f4;
    margin-bottom: 30px;
    font-size: 2rem;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SortBar = styled.div`
  margin-bottom: 20px;
`;

const SortButton = styled(Button)`
  background-color: #555;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  margin-right: 10px;
  transition: background-color 0.3s ease;
  &:hover {
    background-color: #777;
    color: #fff;
  }
  &:focus {
    outline: 2px solid #ffaa00;
    outline-offset: 2px;
  }
`;

const PropertyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
  width: 100%;
  box-sizing: border-box;
`;

const PropertyCard = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  animation: fadeIn 0.5s ease-in;
  border: 2px solid transparent; /* Default border */
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 20px rgba(255, 170, 0, 0.3); /* Glowing effect with OLS theme color */
    border: 2px solid #ffaa00; /* Glowing border on hover */
  }
  @media (max-width: 390px) {
    margin: 0 auto; /* Center cards on very small screens */
    width: 100%;
  }
`;

const PropertyImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const PropertyDetails = styled.div`
  padding: 15px;
  text-align: left;
  color: #333;
`;

const PropertyTitle = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 10px;
  display: block;
  color: #0427f4;
`;

const BuyButton = styled(Button)`
  background-color: #ffaa00;
  color: #0427f4;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: 600;
  transition: background-color 0.3s ease, transform 0.3s ease;
  &:hover {
    background-color: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }
  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const CtaBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 20px;
  margin-bottom: 40px; /* Add bottom margin for spacing */
`;

const CtaButton = styled(Button)`
  background-color: #3552fc;
  color: #fff;
  border: none;
  padding: 8px 16px;
  border-radius: 5px;
  transition: background-color 0.3s ease, transform 0.3s ease;
  &:hover {
    background-color: #2a44d6;
    color: #fff;
    transform: scale(1.05);
  }
  &:focus {
    outline: 2px solid #2a44d6;
    outline-offset: 2px;
  }
`;

const Estate: React.FC = () => {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const properties: Property[] = [
    {
      id: "mount-shasta-2.5-acres",
      price: "104,000 USD",
      details: "2.5 acres land, Mount Shasta, CA, USA",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F104k.usd.2.5a.png?alt=media&token=d51a8d64-df56-45ef-8d60-2ff05c376fe1",
      description:
        "A serene 2.5-acre parcel nestled in the mystical Mount Shasta region, perfect for a retreat or meditation sanctuary. Offers stunning views and proximity to natural wonders.",
      features: ["2.5 acres", "Scenic views", "Quiet location", "Ideal for retreat"],
      location: "Mount Shasta, CA, USA",
    },
    {
      id: "red-sea-85-sqm",
      price: "49,000 USD",
      details: "85 sqm, 3 beds, Red Sea view, 200m walk",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F54k.usd.2.7m.png?alt=media&token=402e5fb6-b1c3-49ae-9655-534fbc7d1b9a",
      description:
        "Spacious 85 sqm apartment with 3 bedrooms and a breathtaking Red Sea view. Just 200 meters from the beach, ideal for relaxation and coastal living.",
      features: ["85 sqm", "3 bedrooms", "Red Sea view", "200m to beach"],
      location: "Red Sea, Egypt",
    },
    {
      id: "red-sea-75-sqm",
      price: "39,000 USD",
      details: "75 sqm, 3 beds, Red Sea view",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/Estate%2FPort%20Ghalib39k.png?alt=media&token=e62349d7-c6d3-416c-96df-e19af95a4a2e",
      description:
        "Cozy 75 sqm apartment featuring 3 bedrooms and a panoramic Red Sea view. Perfect for small families or vacation getaways.",
      features: ["75 sqm", "3 bedrooms", "Red Sea view", "Affordable"],
      location: "Red Sea, Egypt",
    },
    {
      id: "red-sea-54-sqm",
      price: "29,900 USD -> " , 
      details: "54 sqm, 1 bed, Red Sea view, w800",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F28k.usd1.4m.png?alt=media&token=485902f0-c54a-4bf9-9b24-6b1616c41c51",
      description:
        "Compact 54 sqm unit with 1 bedroom and a stunning Red Sea view. Ideal for solo travelers or couples seeking a coastal escape.",
      features: ["54 sqm", "1 bedroom", "Red Sea view", "Coastal access"],
      location: "Red Sea, Egypt",
    },
    {
      id: "red-sea-150-sqm",
      price: "19,000 USD",
      details: "150 sqm, 3 beds, Red Sea view, w700",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F18k.usd880egp.png?alt=media&token=58ca70bf-4b90-45f9-83f8-4bbe0b7a8b2c",
      description:
        "Expansive 150 sqm property with 3 bedrooms and Red Sea views. A budget-friendly option for larger families or investment.",
      features: ["150 sqm", "3 bedrooms", "Red Sea view", "Spacious"],
      location: "Red Sea, Egypt",
    },
    {
      id: "red-sea-135-sqm",
      price: "18,000 USD",
      details: "135 sqm, 3 beds, Red Sea view, w700",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F17k.usd850egp.png?alt=media&token=c730abd0-c92f-4564-bc01-9af831e4728d",
      description:
        "Affordable 135 sqm apartment with 3 bedrooms and Red Sea views. Great value for a spacious coastal home.",
      features: ["135 sqm", "3 bedrooms", "Red Sea view", "Budget-friendly"],
      location: "Red Sea, Egypt",
    },
  ];

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  const sortedProperties = [...properties].sort((a, b) => {
    const priceA = parseFloat(a.price.replace(" USD", "").replace(",", ""));
    const priceB = parseFloat(b.price.replace(" USD", "").replace(",", ""));
    return sortOrder === "asc" ? priceA - priceB : priceB - priceA;
  });

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  useEffect(() => {
    console.log("Estate component mounted");
  }, []);

  return (
    <EstateWrapper>
      <StyledLayout>
        <Helmet>
          <title>Real Estate Marketplace - OneLightSystem OLS</title>
          <meta
            name="description"
            content="Browse radiant real estate listings from OneLightSystem OLS Marketplace. Properties in Mount Shasta, CA, and Red Sea, Egypt, starting at $18,000 USD. Join OLS Sun Light Meditation education at olsme.com/OLSStudentProspect!"
          />
          <meta name="keywords" content="OLS real estate, Sun Light Meditation, Mount Shasta, Red Sea, meditation retreat, coastal property, join OLS" />
          <meta name="author" content="OneLightSystem OLS" />
        </Helmet>
        <HeroSection>
          <Content>
            <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
            <StyledBreadcrumb>
              <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
              <Breadcrumb.Item>Real Estate</Breadcrumb.Item>
            </StyledBreadcrumb>
            <SectionTitle level={2}>OLS iee.aeo Marketplace - Buy Sell Real Estate</SectionTitle>
            <SortBar>
              <SortButton onClick={toggleSortOrder} aria-label="Toggle price sort order">
                Sort by Price {sortOrder === "asc" ? "↑" : "↓"}
              </SortButton>
            </SortBar>
            <PropertyGrid>
              {sortedProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  onClick={() => (window.location.href = `/estate/${property.id}`)}
                  tabIndex={0}
                  onKeyPress={(e) => e.key === "Enter" && (window.location.href = `/estate/${property.id}`)}
                  aria-label={`Property: ${property.details}, Price: ${property.price}`}
                >
                  <PropertyImage src={property.image} alt={property.details} />
                  <PropertyDetails>
                    <PropertyTitle>{property.price}</PropertyTitle>
                    <div>{property.details}</div>
                    <BuyButton href="/register" aria-label={`Buy ${property.details}`}>
                      Buy
                    </BuyButton>
                  </PropertyDetails>
                </PropertyCard>
              ))}
            </PropertyGrid>
            <CtaBar>
              <CtaButton href="mailto:info@olsme.com" aria-label="Expedite Buy via Email">Expedite Buy</CtaButton>
              <CtaButton aria-label="Sign Up"><Link to="/register">Sign Up</Link></CtaButton>
              <CtaButton aria-label="Log In"><Link to="/login">Log In</Link></CtaButton>
            </CtaBar>
          </Content>
        </HeroSection>
      </StyledLayout>
    </EstateWrapper>
  );
};

export default Estate;